import { images } from '@/asset';
import CustomCard from '@/components/customCard';
import RegularText from '@/components/RegularText';
import useUserPlan from '@/hooks/useUserPlan';
import { IDataAnalytic } from '@/pages/Analytics/components/OverTime';
import { IResponseApi } from '@/types/apis/response';
import { Card, IndexTable, Pagination } from '@shopify/polaris';
import { useMemo, useState } from 'react';

interface IDataAnalyticByRuleId {
  [key: number]: IDataAnalytic;
}

interface IProps {
  productAnalytic: IResponseApi.IProductAnalytic[];
  dataAnalytic: IDataAnalytic[];
}

const PER_PAGE = 10;

const ByRules = ({ dataAnalytic, productAnalytic }: IProps) => {
  const [page, setPage] = useState(1);
  const { isFreePlan } = useUserPlan();

  const dataAnalyticFormatted = useMemo(() => {
    const temp: IDataAnalyticByRuleId = {};
    dataAnalytic?.forEach((data) => {
      const { totalAddToCart, totalBannerClicks, totalBannerImpressions, totalHoverBannerETA, totalVisitorsProductPage } = data;
      const currentId = data?.ruleId;
      if (currentId) {
        const currentData = {
          totalAddToCart: totalAddToCart + (temp[currentId]?.totalAddToCart || 0),
          totalBannerClicks: totalBannerClicks + (temp[currentId]?.totalBannerClicks || 0),
          totalBannerImpressions: totalBannerImpressions + (temp[currentId]?.totalBannerImpressions || 0),
          totalHoverBannerETA: totalHoverBannerETA + (temp[currentId]?.totalHoverBannerETA || 0),
          totalVisitorsProductPage: totalVisitorsProductPage + (temp[currentId]?.totalVisitorsProductPage || 0),
        };
        temp[currentId] = currentData;
      }
    });
    return temp;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(productAnalytic), JSON.stringify(dataAnalytic)]);

  const rowMarkup = useMemo(() => {
    if (productAnalytic?.length) {
      const currentData = productAnalytic?.slice((page - 1) * PER_PAGE, (page - 1) * PER_PAGE + PER_PAGE);
      return currentData.map((item, index) => {
        const current = dataAnalyticFormatted[item?.ruleId];
        const ATC = (((current?.totalAddToCart || 0) / (current?.totalVisitorsProductPage || 1)) * 100).toFixed(2);
        const CTR = (((current?.totalBannerClicks || 0) / (current?.totalBannerImpressions || 1)) * 100).toFixed(2);
        const Engage = (
          (((current?.totalAddToCart || 0) + (current?.totalHoverBannerETA || 0) + (current?.totalBannerClicks || 0)) /
            (current?.totalBannerImpressions || 1)) *
          100
        ).toFixed(2);
        return (
          <IndexTable.Row id={item?.ruleId + ''} key={item?.ruleId} position={index}>
            <IndexTable.Cell>
              <RegularText>{item?.ruleName}</RegularText>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <RegularText>{item?.totalProducts || 0}</RegularText>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <RegularText>{dataAnalyticFormatted[item?.ruleId]?.totalVisitorsProductPage || 0}</RegularText>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <RegularText>{dataAnalyticFormatted[item?.ruleId]?.totalBannerImpressions || 0}</RegularText>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <RegularText>{dataAnalyticFormatted[item?.ruleId]?.totalHoverBannerETA || 0}</RegularText>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <RegularText>{dataAnalyticFormatted[item?.ruleId]?.totalBannerClicks || 0}</RegularText>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <RegularText>{dataAnalyticFormatted[item?.ruleId]?.totalAddToCart || 0}</RegularText>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <RegularText>{ATC || 0}</RegularText>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <RegularText>{CTR || 0}</RegularText>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <RegularText>{Engage || 0}</RegularText>
            </IndexTable.Cell>
          </IndexTable.Row>
        );
      });
    }
    return <></>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(productAnalytic), JSON.stringify(dataAnalyticFormatted), page]);

  const totalPage = Math.ceil((productAnalytic?.length || 0) / PER_PAGE);

  return (
    <CustomCard cardClassName={isFreePlan ? 'disabled' : ''} title={<b style={{ fontSize: '16px' }}>Performance by rules</b>}>
      <div className="mt-16">
        <Card padding={'0'}>
          {productAnalytic?.length > 0 ? (
            <IndexTable
              itemCount={productAnalytic?.length || 0}
              headings={[
                { title: 'Rule name' },
                { title: 'Products' },
                { title: 'PDP visits' },
                { title: 'Impressions' },
                { title: 'Hovers' },
                { title: 'Clicks' },
                { title: 'ATC' },
                { title: '%ATC' },
                { title: '%CTR' },
                { title: '%Engage' },
              ]}
              selectable={false}
            >
              {rowMarkup}
            </IndexTable>
          ) : (
            <div className="d-flex flex-column content-center" style={{ paddingBottom: '45px' }}>
              <div className="text-center">
                <img src={images.emptyDataAnalytics} alt="empty data" width={230} height={230} />
              </div>
              <div className="mt-16 text-center">
                <b style={{ fontSize: '0.875rem' }}>No data available yet</b>
              </div>
            </div>
          )}
        </Card>
        {totalPage ? (
          <div className="mt-16 d-flex content-center">
            <Pagination
              hasNext={totalPage > page}
              hasPrevious={page > 1}
              label={`${page} of ${totalPage}`}
              onNext={() => {
                setPage((prev) => prev + 1);
              }}
              onPrevious={() => {
                setPage((prev) => prev - 1);
              }}
            />
          </div>
        ) : null}
      </div>
    </CustomCard>
  );
};

export default ByRules;
